import React, { useState, useRef, useMemo } from "react";
import {
  Modal,
  Typography,
  Slider,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  ResponsiveContainer,
  LineChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Line,
  Brush,
} from "recharts";
import html2canvas from "html2canvas";
import Datatable from "./dataTable";
import "./leftbar.css";
import "jspdf-autotable";

const HelpModal = ({ open, handleClose }) => (
  <Modal open={open} onClose={handleClose}>
    <div className="help-modal-container">
      <div className="help-modal-close">
        <Button variant="contained" color="secondary" onClick={handleClose}>
          Close
        </Button>
      </div>
      <Typography variant="h5">📌 Zoom and Hover Guide</Typography>

      <Typography variant="h6">🔍 Zoom Controls</Typography>
      <ul>
        <li><strong>Zoom In:</strong> Adjust slider or brush below chart.</li>
        <li><strong>Reset Zoom:</strong> Click Reset Zoom.</li>
        <li><strong>Save Image:</strong> Download current chart view.</li>
      </ul>

      <Typography variant="h6">🖱️ Hover Feature</Typography>
      <ul>
        <li><strong>Tooltip:</strong> Hover to view values.</li>
        <li><strong>Interactive Dots:</strong> Highlight on hover.</li>
      </ul>
    </div>
  </Modal>
);

const Datalog = ({
  selectedMarker,
  selectedMetric,
  downloadAsCSV,
  downloadAsPDF,
  handleCloseModal,
  resetZoomDatalogs,
  formatDate,
  setHelpOpen,
  helpOpen,
  dataLimitDatalogs,
  filteredDataDatalogs,
  visibleDataDatalogs,
  datalogs,
  sortConfig,
  handleDataLimitChangeDatalogs,
  handleSort,
  CustomLegend,
}) => {
  const chartRef = useRef();

  // Local zoom state
  const [zoomLevel, setZoomLevel] = useState(0);

  const handleZoomSlider = (event, newValue) => {
    setZoomLevel(newValue);
  };

  const handleDownload = () => {
    if (chartRef.current) {
      html2canvas(chartRef.current).then((canvas) => {
        const link = document.createElement("a");
        link.download = "rawDatalog.png";
        link.href = canvas.toDataURL();
        link.click();
      });
    }
  };

  const zoomedData = useMemo(() => {
    if (!filteredDataDatalogs || filteredDataDatalogs.length === 0) return [];
  
    const zoomFactor = (100 - zoomLevel) / 100; // reversed zoom
    const visibleCount = Math.max(
      1,
      Math.floor(filteredDataDatalogs.length * zoomFactor)
    );
  
    return filteredDataDatalogs.slice(-visibleCount); // latest N points
  }, [filteredDataDatalogs, zoomLevel]);
  

  return (
    <div className="modal">
      <div className="modal-overlay" />
      <div className="modal-row">
        <div className="modal-content left-content">
          <div className="button-group">
            <Button variant="contained" onClick={downloadAsCSV} className="blue-button">
              Download as CSV
            </Button>
            <Button variant="contained" onClick={downloadAsPDF} className="green-button">
              Download as PDF
            </Button>
            <Button variant="contained" color="secondary" onClick={() => setHelpOpen(true)}>
              Help
            </Button>
            <HelpModal open={helpOpen} handleClose={() => setHelpOpen(false)} />
          </div>

          <div className="graph-container">
            <h3>WTR (Water Level) VS mTm (Measurement Time)</h3>

            <div className="button-group">
              <Button variant="contained" onClick={() => setZoomLevel(0)} className="zoom-button">
                Reset Zoom
              </Button>
              <Button variant="contained" onClick={handleDownload} className="zoom-button">
                Save Image
              </Button>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "10px", width: "100%" }}>
              <Slider
                value={zoomLevel}
                onChange={handleZoomSlider}
                aria-labelledby="zoom-slider"
                min={5}
                max={100}
                step={5}
                valueLabelDisplay="auto"
                style={{ flex: 1 }}
              />
              <h3 style={{ minWidth: "50px", margin: 0 }}>{zoomLevel}%</h3>
            </div>

            <div className="filter-container">
              <h3 className="filter-label">Filter Data: </h3>
              <FormControl variant="outlined" size="small" className="filter-dropdown">
                <InputLabel>Data Points</InputLabel>
                <Select
                  value={dataLimitDatalogs}
                  onChange={handleDataLimitChangeDatalogs}
                  label="Data Points"
                >
                  <MenuItem value={10}>Last 10</MenuItem>
                  <MenuItem value={50}>Last 50</MenuItem>
                  <MenuItem value={100}>Last 100</MenuItem>
                  <MenuItem value={visibleDataDatalogs.length}>All</MenuItem>
                </Select>
              </FormControl>
            </div>

            <div ref={chartRef}>
              <ResponsiveContainer width="100%" height={350}>
                <LineChart
                  data={zoomedData}
                  margin={{ top: 10, right: 30, left: 10, bottom: 10 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="mTm"
                    tickFormatter={(tick) => new Date(tick).toLocaleDateString()}
                    label={{
                      value: "Measurement Time (mTm)",
                      position: "insideBottomRight",
                      offset: -10,
                    }}
                  />
                  <YAxis
                    domain={[0, 12]}
                    label={{
                      value: selectedMetric.toUpperCase(),
                      angle: -90,
                      position: "insideLeft",
                    }}
                  />
                  <Tooltip
                    contentStyle={{ color: "#fff" }}
                    labelFormatter={(value) => `Time: ${formatDate(value)}`}
                    formatter={(value, name) => [`${value}`, `${name.toUpperCase()}`]}
                  />
                  <Legend content={<CustomLegend />} />
                  <Line
                    type="monotone"
                    dataKey={selectedMetric}
                    stroke="#000000"
                    strokeWidth={2}
                    activeDot={{
                      r: 6,
                      fill: "blue",
                      stroke: "black",
                      strokeWidth: 2,
                    }}
                  />
                  <Brush
                    dataKey="mTm"
                    height={50}
                    stroke="#8884d8"
                    tickFormatter={(tick) => new Date(tick).toLocaleString()}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <div className="modal-content right-content">
          <Button variant="contained" onClick={handleCloseModal} className="red-button right-align">
            Close Window
          </Button>
          <Datatable
            selectedMarker={selectedMarker}
            formatDate={formatDate}
            sortConfig={sortConfig}
            handleSort={handleSort}
            datalogs={datalogs}
          />
        </div>
      </div>
    </div>
  );
};

export default Datalog;
